import { Box, SwipeableDrawer, Typography, useMediaQuery } from '@mui/material';
import { Modal } from '@mui/material';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { CharityType } from 'store/causes/types';
import { useAppSelector } from 'store/hooks';
import { selectedCharitiesState } from 'store/charities/charities.slice';
import { getPrettyBudget, getPrettyWebsite } from 'utils/utils';
import DynamicGoogleIcon from '../dynamic-icon/dynamicGoogleIcon';
import RatingDetails from './RatingDetails';
import LastUpdates from './LastUpdates';
import { BackIconButton } from '../common';

interface ICharityModalProps {
   charityModalData: CharityType | null;
   handleClose: () => void;
}

const CharityModal: React.FC<ICharityModalProps> = ({ charityModalData, handleClose }) => {
   const { impactReports } = useAppSelector(selectedCharitiesState);

   const { t } = useTranslation();

   const getFilteredImpactReports = () => {
      if (impactReports && charityModalData) {
         return impactReports.filter((report) => report.charityId === charityModalData?.id);
      }
      return [];
   };

   const filteredImpactReports = getFilteredImpactReports();

   const screenWidth = window.innerWidth;
   const mobileImageHeight = (screenWidth * 190) / 390;
   const indicatorImageHeight = (screenWidth * 170) / 390;
   const isDesktop = useMediaQuery('(min-width:741px)');

   return isDesktop ? (
      <Modal
         sx={{
            overflowY: 'scroll',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'center',
         }}
         open={charityModalData !== null}
         onClose={handleClose}
      >
         <Box
            sx={{
               py: 8.75,
               px: 2.5,
               height: 'fit-content',
               '&:focus': {
                  outline: 'none',
               },
               '&:focus-visible': {
                  outline: 'none',
               },
            }}
         >
            <Box
               sx={{
                  maxWidth: '800px',
                  minWidth: '700px',
                  height: 'fit-content',
                  borderRadius: '6px',
                  overflowY: 'auto',
                  position: 'relative',
                  backgroundColor: '#ffffff',
               }}
            >
               <Box
                  sx={{
                     width: '100%',
                     height: '220px',
                     overflow: 'hidden',
                     display: 'flex',
                     justifyContent: 'center',
                     alignItems: 'start',
                  }}
               >
                  <BackIconButton
                     sx={{
                        position: 'absolute',
                        border: '1px solid #E5E5E5',
                        top: '20px',
                        right: '20px',
                        zIndex: '10000',
                     }}
                     onClick={handleClose}
                  >
                     <DynamicGoogleIcon iconColor='#999999' iconName='close' />
                  </BackIconButton>

                  <img
                     style={{
                        width: '100%',
                        height: 'auto',
                     }}
                     src={charityModalData?.image}
                     alt=''
                  />
               </Box>

               <Box
                  sx={{
                     display: 'flex',
                     flexDirection: 'row',
                     columnGap: 3.75,
                     px: 2.5,
                     pb: 2.5,
                     mt: '-20px',
                  }}
               >
                  {/* Left column */}
                  <Box sx={{ maxWidth: '365px', width: '50%' }}>
                     {/* Charity icon */}
                     <Box
                        sx={{
                           alignSelf: 'flex-start',
                           width: 80,
                           minWidth: 80,
                           height: 80,
                           minHeight: 80,
                           borderRadius: '6px',
                           overflow: 'hidden',
                           border: '1px solid #D3D2CE',
                           boxSizing: 'border-box',
                        }}
                     >
                        <img
                           style={{
                              width: '100%',
                              height: 'auto',
                           }}
                           src={charityModalData?.icon}
                           alt=''
                        />
                     </Box>

                     <Box
                        sx={{
                           marginTop: '10px',
                        }}
                     >
                        <Box
                           sx={{
                              marginBottom: '20px',
                           }}
                        >
                           <Typography
                              sx={{
                                 fontSize: '20px',
                                 lineHeight: '24px',
                                 fontWeight: 700,
                              }}
                           >
                              {charityModalData?.title}
                           </Typography>
                           <Typography
                              sx={{
                                 color: '#666666',
                                 fontSize: '16px',
                                 lineHeight: '19px',
                                 fontWeight: 500,
                              }}
                           >
                              {charityModalData?.shortDescription}
                           </Typography>
                        </Box>

                        <Typography
                           sx={{
                              color: '#666666',
                              fontSize: '16px',
                              lineHeight: '19px',
                              fontWeight: 500,
                           }}
                        >
                           {charityModalData?.description}
                        </Typography>
                     </Box>

                     {/* imapct image */}
                     {charityModalData?.indicatorImage && (
                        <Box sx={{ width: '100%', mt: 2.5 }}>
                           <Box
                              sx={{
                                 p: 1.25,
                                 display: 'flex',
                                 flexDirection: 'column',
                                 justifyContent: 'end',
                                 borderRadius: '6px',
                                 rowGap: '6px',
                                 width: '100%',
                                 height: '190px',
                                 backgroundSize: 'cover',
                                 backgroundPosition: 'center',
                                 backgroundImage: `linear-gradient(180deg, #00000000 0%, #00000050 50%, #00000050 50%), url(${charityModalData.indicatorImage})`,
                              }}
                           >
                              <Typography
                                 sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 700, color: '#FFFFFF' }}
                              >
                                 Did you know?
                              </Typography>
                              <Typography
                                 sx={{ fontSize: '24px', lineHeight: '24px', fontWeight: 700, color: '#FFFFFF' }}
                              >
                                 {charityModalData.publicIndicator}
                              </Typography>
                           </Box>
                        </Box>
                     )}
                  </Box>
                  {/* Right column */}
                  <Box sx={{ maxWidth: '365px', width: '50%' }}>
                     <Box sx={{ width: '100%' }}>
                        <Box
                           sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              px: 2,
                              py: 0.75,
                              border: '1px solid #E5E5E5',
                              borderRadius: '6px',
                              backgroundColor: '#ffffff',
                           }}
                        >
                           {/* details rows */}
                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: 'row',
                                 justifyContent: 'space-between',
                                 py: 1.25,
                                 borderBottom: '1px solid #E5E5E5',
                              }}
                           >
                              <Typography
                                 sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 600, color: '#999999' }}
                              >
                                 Founded
                              </Typography>
                              <Typography
                                 sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 500, color: '#333333' }}
                              >
                                 {charityModalData?.yearFounded}
                              </Typography>
                           </Box>

                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: 'row',
                                 justifyContent: 'space-between',
                                 py: 1.25,
                                 borderBottom: '1px solid #E5E5E5',
                              }}
                           >
                              <Typography
                                 sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 600, color: '#999999' }}
                              >
                                 Yearly Budget
                              </Typography>
                              <Typography
                                 sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 500, color: '#333333' }}
                              >
                                 {getPrettyBudget(charityModalData?.budget)}
                              </Typography>
                           </Box>

                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: 'row',
                                 justifyContent: 'space-between',
                                 py: 1.25,
                                 borderBottom: '1px solid #E5E5E5',
                              }}
                           >
                              <Typography
                                 sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 600, color: '#999999' }}
                              >
                                 EIN
                              </Typography>
                              <Typography
                                 sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 500, color: '#333333' }}
                              >
                                 {charityModalData?.ein}
                              </Typography>
                           </Box>

                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: 'row',
                                 justifyContent: 'space-between',
                                 alignItems: 'center',
                                 py: 1.25,
                                 borderBottom: '1px solid #E5E5E5',
                              }}
                           >
                              <Typography
                                 sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 600, color: '#999999' }}
                              >
                                 Website
                              </Typography>
                              <Box
                                 sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'row', columnGap: '4px' }}
                                 onClick={() => window.open(charityModalData?.website, '_blank')}
                              >
                                 <Typography
                                    sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 500, color: '#333333' }}
                                 >
                                    {getPrettyWebsite(charityModalData?.website)}
                                 </Typography>
                                 <DynamicGoogleIcon
                                    iconSizeVariant='small'
                                    iconColor='#333333'
                                    iconName='open_in_new'
                                 />
                              </Box>
                           </Box>

                           <Box
                              sx={{
                                 display: 'flex',
                                 flexDirection: 'row',
                                 justifyContent: 'space-between',
                                 alignItems: 'center',
                                 py: 1.25,
                                 borderBottom: '1px solid #E5E5E5',
                              }}
                           >
                              <Typography
                                 sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 600, color: '#999999' }}
                              >
                                 Annual report
                              </Typography>
                              <Box
                                 sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'row', columnGap: '4px' }}
                                 onClick={() => window.open(charityModalData?.annualReport, '_blank')}
                              >
                                 <Typography
                                    sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 500, color: '#333333' }}
                                 >
                                    Download
                                 </Typography>
                                 <DynamicGoogleIcon iconSizeVariant='small' iconColor='#333333' iconName='download' />
                              </Box>
                           </Box>

                           <RatingDetails cnRank={charityModalData?.cnRank} candidRank={charityModalData?.candidRank} />
                        </Box>
                        {filteredImpactReports.length > 0 && (
                           <LastUpdates fixedPcitureHeight={140} impactReports={filteredImpactReports} />
                        )}
                        <Box sx={{ mt: 2.5 }}>
                           <Typography sx={{ fontSize: '14px', lineHeight: '17px', color: '#666666' }}>
                              * {t('impactUpdates.disclaimer')}
                           </Typography>
                        </Box>
                     </Box>
                  </Box>
               </Box>
            </Box>
         </Box>
      </Modal>
   ) : (
      <SwipeableDrawer
         sx={{
            '& .MuiDrawer-paper': {
               borderTopLeftRadius: '10px',
               borderTopRightRadius: '10px',
               maxHeight: '85vh',
               height: 'auto',
            },
         }}
         anchor='bottom'
         open={charityModalData !== null}
         onClose={handleClose}
         onOpen={() => undefined}
         keepMounted
      >
         <Box
            sx={{
               width: '100%',
               maxHeight: '85vh',
               borderTopLeftRadius: '10px',
               borderTopRightRadius: '10px',
               overflowY: 'auto',
               backgroundColor: '#F7F7F7',
               display: 'flex',
               flexDirection: 'column',
               alignItems: 'center',
               pb: 5,
               '&::-webkit-scrollbar': {
                  display: 'none',
               },
               '&::-webkit-scrollbar-track': {
                  background: '#f1f1f1',
               },
               '&::-webkit-scrollbar-thumb': {
                  background: '#888',
                  borderRadius: '4px',
               },
            }}
         >
            <BackIconButton
               sx={{
                  border: '1px solid #E5E5E5',
                  position: 'absolute',
                  top: '30px',
                  right: '20px',
                  zIndex: '10000',
               }}
               onClick={handleClose}
            >
               <DynamicGoogleIcon iconColor='#999999' iconName='close' />
            </BackIconButton>

            {/* Top handle */}
            <Box
               sx={{
                  border: '1px solid #666666',
                  top: '10px',
                  backgroundColor: '#000000',
                  position: 'absolute',
                  width: '120px',
                  height: '5px',
                  borderRadius: '5px',
               }}
            />

            {/* Charity image */}
            <Box
               sx={{
                  width: '100%',
                  minHeight: mobileImageHeight,
                  overflow: 'hidden',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
               }}
            >
               <img
                  style={{
                     width: '100%',
                     height: 'auto',
                  }}
                  src={charityModalData?.image}
                  alt=''
               />
            </Box>
            {/* Charity icon */}
            <Box
               sx={{
                  alignSelf: 'flex-start',
                  mt: '-60px',
                  width: 80,
                  minWidth: 80,
                  height: 80,
                  minHeight: 80,
                  borderRadius: '6px',
                  overflow: 'hidden',
                  border: '1px solid #D3D2CE',
                  boxSizing: 'border-box',
                  ml: 2.5,
               }}
            >
               <img
                  style={{
                     width: '100%',
                     height: 'auto',
                  }}
                  src={charityModalData?.icon}
                  alt=''
               />
            </Box>

            <Box
               sx={{
                  px: '20px',
                  marginTop: '10px',
               }}
            >
               <Box
                  sx={{
                     marginBottom: '20px',
                  }}
               >
                  <Typography
                     sx={{
                        fontSize: '20px',
                        lineHeight: '24px',
                        fontWeight: 700,
                     }}
                  >
                     {charityModalData?.title}
                  </Typography>
                  <Typography
                     sx={{
                        color: '#666666',
                        fontSize: '16px',
                        lineHeight: '19px',
                        fontWeight: 500,
                     }}
                  >
                     {charityModalData?.shortDescription}
                  </Typography>
               </Box>

               <Typography
                  sx={{
                     color: '#666666',
                     fontSize: '16px',
                     lineHeight: '19px',
                     fontWeight: 500,
                  }}
               >
                  {charityModalData?.description}
               </Typography>
            </Box>

            {/* imapct image */}
            {charityModalData?.indicatorImage && (
               <Box sx={{ width: '100%', px: 2.5, mt: 2.5 }}>
                  <Box
                     sx={{
                        p: 1.25,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'end',
                        borderRadius: '6px',
                        rowGap: '6px',
                        width: '100%',
                        height: indicatorImageHeight,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                        backgroundImage: `linear-gradient(180deg, #00000000 0%, #00000050 50%, #00000050 50%), url(${charityModalData.indicatorImage})`,
                     }}
                  >
                     <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 700, color: '#FFFFFF' }}>
                        Did you know?
                     </Typography>
                     <Typography sx={{ fontSize: '24px', lineHeight: '24px', fontWeight: 700, color: '#FFFFFF' }}>
                        {charityModalData.publicIndicator}
                     </Typography>
                  </Box>
               </Box>
            )}

            {/* Charity details */}
            <Box sx={{ width: '100%', px: 2.5, mt: 2.5 }}>
               <Typography sx={{ fontSize: '20px', lineHeight: '24px', fontWeight: 700, color: '#333333' }}>
                  Charity details
               </Typography>
            </Box>
            <Box sx={{ width: '100%', px: 2.5, mt: 2.5 }}>
               <Box
                  sx={{
                     display: 'flex',
                     flexDirection: 'column',
                     px: 2,
                     py: 0.75,
                     border: '1px solid #E5E5E5',
                     borderRadius: '6px',
                     backgroundColor: '#ffffff',
                  }}
               >
                  {/* details rows */}
                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        py: 1.25,
                        borderBottom: '1px solid #E5E5E5',
                     }}
                  >
                     <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 600, color: '#999999' }}>
                        Founded
                     </Typography>
                     <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 500, color: '#333333' }}>
                        {charityModalData?.yearFounded}
                     </Typography>
                  </Box>

                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        py: 1.25,
                        borderBottom: '1px solid #E5E5E5',
                     }}
                  >
                     <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 600, color: '#999999' }}>
                        Yearly Budget
                     </Typography>
                     <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 500, color: '#333333' }}>
                        {getPrettyBudget(charityModalData?.budget)}
                     </Typography>
                  </Box>

                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        py: 1.25,
                        borderBottom: '1px solid #E5E5E5',
                     }}
                  >
                     <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 600, color: '#999999' }}>
                        EIN
                     </Typography>
                     <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 500, color: '#333333' }}>
                        {charityModalData?.ein}
                     </Typography>
                  </Box>

                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1.25,
                        borderBottom: '1px solid #E5E5E5',
                     }}
                  >
                     <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 600, color: '#999999' }}>
                        Website
                     </Typography>
                     <Box
                        sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'row', columnGap: '4px' }}
                        onClick={() => window.open(charityModalData?.website, '_blank')}
                     >
                        <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 500, color: '#333333' }}>
                           {getPrettyWebsite(charityModalData?.website)}
                        </Typography>
                        <DynamicGoogleIcon iconSizeVariant='small' iconColor='#333333' iconName='open_in_new' />
                     </Box>
                  </Box>

                  <Box
                     sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        py: 1.25,
                        borderBottom: '1px solid #E5E5E5',
                     }}
                  >
                     <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 600, color: '#999999' }}>
                        Annual report
                     </Typography>
                     <Box
                        sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'row', columnGap: '4px' }}
                        onClick={() => window.open(charityModalData?.annualReport, '_blank')}
                     >
                        <Typography sx={{ fontSize: '14px', lineHeight: '17px', fontWeight: 500, color: '#333333' }}>
                           Download
                        </Typography>
                        <DynamicGoogleIcon iconSizeVariant='small' iconColor='#333333' iconName='download' />
                     </Box>
                  </Box>

                  <RatingDetails cnRank={charityModalData?.cnRank} candidRank={charityModalData?.candidRank} />
               </Box>
               {filteredImpactReports.length > 0 && <LastUpdates impactReports={filteredImpactReports} />}
               <Box sx={{ mt: 2.5 }}>
                  <Typography sx={{ fontSize: '14px', lineHeight: '17px', color: '#666666' }}>
                     * {t('impactUpdates.disclaimer')}
                  </Typography>
               </Box>
            </Box>
         </Box>
      </SwipeableDrawer>
   );
};

export default CharityModal;
